/**
 * Importación del libro de guías de despacho.
 *
 * Lee el formato real de `Revision 2 GD KM 13 NV 17.xlsx`: una hoja por planta
 * con las columnas FECHA, N° DE GUIA, N° REMITO, TURNO, USUARIO, PRODUCTO, m3,
 * Grafo, [PROYECTO], DESTINO, seis horarios, EQUIPO, OPERADOR, Op Planta,
 * Solicitado por y observaciones.
 *
 * Corrige al vuelo los defectos detectados en el análisis:
 *  E1 — en NV 17, desde cierta fila las columnas C y D vienen invertidas
 *       (el turno en «N° REMITO» y el remito en «TURNO»). Se detecta por
 *       contenido, no por número de fila.
 *  E5 — «NULA» en la cantidad: la guía entra como ANULADA con 0 m³, y conserva
 *       su folio para el control del talonario.
 *  E6 — horas como fracción de día que retroceden al cruzar la medianoche:
 *       se reconstruyen como instantes reales sumando el día.
 *  E9 — nombres escritos de muchas formas: se resuelven contra los catálogos
 *       y sus alias.
 */

import ExcelJS from 'exceljs';
import { COL } from './firebase';
import { guardarLote, listar } from './repositorio';
import { metaNueva, uuidv7, type DocGuia } from './tiposDatos';
import type { DocCatalogo, DocPlanta, DocProducto } from './tiposDatos';
import { calcularTiempos } from '@/domain/tiempos';
import { calcularRecargos, totalRecargosUf } from '@/domain/recargos';
import { parsearProducto, normalizarCodigo } from '@/domain/producto';
import { precioVigente } from '@/domain/precios';
import type { CodigoPlanta, ItemContrato, Turno } from '@/domain/tipos';

// ── Utilidades ────────────────────────────────────────────────────────────

const TURNOS_VALIDOS = new Set(['A', 'C', 'TA']);

/** ¿El texto es una marca de anulación? */
function esNula(v: unknown): boolean {
  return typeof v === 'string' && /^NUL[AO]S?\s*$/i.test(v.trim());
}

function texto(v: ExcelJS.CellValue): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') return v.trim() || null;
  if (typeof v === 'number') return String(v);
  if (v instanceof Date) return v.toISOString();
  if (typeof v === 'object' && 'text' in v) return String(v.text).trim() || null;
  if (typeof v === 'object' && 'result' in v) return texto(v.result as ExcelJS.CellValue);
  return String(v).trim() || null;
}

function numero(v: ExcelJS.CellValue): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'object' && v !== null && 'result' in v) {
    return numero((v as { result: ExcelJS.CellValue }).result);
  }
  if (typeof v === 'string') {
    const n = Number(v.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Serial de Excel o Date → 'yyyy-MM-dd'. */
function aFecha(v: ExcelJS.CellValue): string | null {
  if (v instanceof Date) {
    const mm = String(v.getMonth() + 1).padStart(2, '0');
    const dd = String(v.getDate()).padStart(2, '0');
    return `${v.getFullYear()}-${mm}-${dd}`;
  }
  const n = numero(v);
  if (n === null || n < 1 || n > 80000) return null;
  // Serial de Excel: día 1 = 1900-01-01, con el salto de 1900 ya considerado.
  const d = new Date(Date.UTC(1899, 11, 30) + n * 86_400_000);
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${d.getUTCFullYear()}-${mm}-${dd}`;
}

/** Hora como fracción de día (0..1) o Date → minutos desde medianoche. */
function aMinutos(v: ExcelJS.CellValue): number | null {
  if (v instanceof Date) return v.getUTCHours() * 60 + v.getUTCMinutes();
  const n = numero(v);
  if (n === null) return null;
  const frac = n >= 1 ? n - Math.floor(n) : n;
  if (frac < 0 || frac >= 1) return null;
  return Math.round(frac * 1440);
}

/**
 * Reconstruye los seis instantes del ciclo.
 * Si una marca retrocede respecto de la anterior, cruzó la medianoche.
 */
function reconstruirCiclo(
  fecha: string,
  minutos: (number | null)[],
): { instantes: (Date | null)[]; cruces: number } {
  const [a, m, d] = fecha.split('-').map(Number);
  const instantes: (Date | null)[] = [];
  let dias = 0;
  let previo: number | null = null;
  let cruces = 0;

  for (const min of minutos) {
    if (min === null) {
      instantes.push(null);
      continue;
    }
    if (previo !== null && min < previo) {
      dias++;
      cruces++;
    }
    const dt = new Date(a!, m! - 1, d! + dias, 0, 0, 0, 0);
    dt.setMinutes(min);
    instantes.push(dt);
    previo = min;
  }
  return { instantes, cruces };
}

/** Índice de búsqueda por nombre y por alias, todo normalizado. */
function indice(cat: DocCatalogo[]): Map<string, DocCatalogo> {
  const m = new Map<string, DocCatalogo>();
  const clave = (s: string) =>
    s
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
  for (const c of cat) {
    for (const nombre of [c.nombre, c.codigo, ...(c.alias ?? [])]) {
      if (!nombre) continue;
      const k = clave(nombre);
      if (k && !m.has(k)) m.set(k, c);
    }
  }
  return m;
}

const clave = (s: string) =>
  s
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

// ── Resultado ─────────────────────────────────────────────────────────────

export interface InformeImportacion {
  archivo: string;
  hojas: { nombre: string; planta: CodigoPlanta | null; filas: number; importadas: number }[];
  importadas: number;
  omitidas: number;
  anuladas: number;
  duplicadas: { folio: number; planta: string }[];
  columnasCorregidas: number;
  crucesMedianoche: number;
  sinFecha: number;
  sinProducto: string[];
  sinCliente: string[];
  tiemposIncompletos: number;
  bloquesSobrestadia: number;
  m3Total: number;
}

/**
 * Hojas auxiliares del libro que no son de guías y quedan fuera de alcance:
 * `HORARIOS KM13` es un histograma derivado y `Hoja3` una libreta de stock.
 */
const HOJAS_EXCLUIDAS = [/^HORARIOS/, /^HOJA\d*$/, /^RESUMEN$/, /^PANEL$/];

/** Deduce la planta a partir del nombre de la hoja. */
function plantaDeHoja(nombre: string): CodigoPlanta | null {
  const n = clave(nombre);
  if (HOJAS_EXCLUIDAS.some((re) => re.test(n))) return null;
  if (n.includes('NV17')) return 'NV 17';
  if (n.includes('KM13')) return 'KM 13';
  if (n.includes('VALLE')) return 'VALLE';
  return null;
}

/** Encuentra la fila de encabezados y mapea nombre de columna → número. */
function mapearColumnas(hoja: ExcelJS.Worksheet): { fila: number; cols: Map<string, number> } | null {
  for (let f = 1; f <= Math.min(10, hoja.rowCount); f++) {
    const fila = hoja.getRow(f);
    const cols = new Map<string, number>();
    fila.eachCell((celda, col) => {
      const t = texto(celda.value);
      if (t) cols.set(clave(t), col);
    });
    if (cols.has(clave('N° DE GUIA')) || cols.has(clave('N DE GUIA')) || cols.has('NDEGUIA')) {
      return { fila: f, cols };
    }
  }
  return null;
}

const buscarCol = (cols: Map<string, number>, ...nombres: string[]): number | null => {
  for (const n of nombres) {
    const c = cols.get(clave(n));
    if (c !== undefined) return c;
  }
  return null;
};

export async function importarLibroGuias(
  archivo: File,
  alProgresar?: (pct: number, etapa: string) => void,
): Promise<InformeImportacion> {
  alProgresar?.(2, 'Leyendo el archivo…');

  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await archivo.arrayBuffer());

  alProgresar?.(12, 'Cargando catálogos…');

  const [plantas, clientes, productos, equipos, personas, centros, destinos, items] =
    await Promise.all([
      listar<DocPlanta>(COL.plantas),
      listar<DocCatalogo>(COL.clientes),
      listar<DocProducto>(COL.productos),
      listar<DocCatalogo>(COL.equipos),
      listar<DocCatalogo>(COL.personas),
      listar<DocCatalogo>(COL.centrosCosto),
      listar<DocCatalogo>(COL.destinos),
      listar<ItemContrato>(COL.itemsContrato),
    ]);

  const iCliente = indice(clientes);
  const iProducto = indice(productos as unknown as DocCatalogo[]);
  const iEquipo = indice(equipos);
  const iPersona = indice(personas);
  const iCentro = indice(centros);
  const iDestino = indice(destinos);

  const itemPorCodigo = new Map(items.map((i) => [i.codigo, i]));
  const buscarItem = (codigo: string) => {
    const it = itemPorCodigo.get(codigo);
    return it ? { ...it, precioUf: precioVigente(it) } : null;
  };
  const itemDePlanta: Record<CodigoPlanta, string> = {
    'NV 17': '1.8',
    'KM 13': '2.8',
    VALLE: '3.24',
  };

  const existentes = await listar<DocGuia>(COL.guias);
  const yaCargado = new Set(
    existentes.filter((g) => !g.eliminadoEn).map((g) => `${g.plantaCodigo}|${g.folio}`),
  );

  const inf: InformeImportacion = {
    archivo: archivo.name,
    hojas: [],
    importadas: 0,
    omitidas: 0,
    anuladas: 0,
    duplicadas: [],
    columnasCorregidas: 0,
    crucesMedianoche: 0,
    sinFecha: 0,
    sinProducto: [],
    sinCliente: [],
    tiemposIncompletos: 0,
    bloquesSobrestadia: 0,
    m3Total: 0,
  };

  const nuevas: DocGuia[] = [];
  const sinProducto = new Set<string>();
  const sinCliente = new Set<string>();

  const hojasUtiles = wb.worksheets.filter((h) => plantaDeHoja(h.name) !== null);
  if (hojasUtiles.length === 0) {
    throw new Error(
      'No se reconoció ninguna hoja de planta. Se esperan hojas llamadas «NV 17», «KM 13» o «VALLE».',
    );
  }

  let hojaN = 0;
  for (const hoja of hojasUtiles) {
    hojaN++;
    const planta = plantaDeHoja(hoja.name)!;
    const docPlanta = plantas.find((p) => p.codigoPlanta === planta);
    const encabezado = mapearColumnas(hoja);

    if (!encabezado) {
      inf.hojas.push({ nombre: hoja.name, planta, filas: 0, importadas: 0 });
      continue;
    }
    const { fila: filaEnc, cols } = encabezado;

    const c = {
      fecha: buscarCol(cols, 'FECHA'),
      folio: buscarCol(cols, 'N° DE GUIA', 'N DE GUIA', 'NRO GUIA'),
      remito: buscarCol(cols, 'N° REMITO', 'N REMITO'),
      turno: buscarCol(cols, 'TURNO'),
      cliente: buscarCol(cols, 'USUARIO', 'CLIENTE'),
      producto: buscarCol(cols, 'PRODUCTO'),
      m3: buscarCol(cols, 'm3', 'M3'),
      grafo: buscarCol(cols, 'Grafo', 'CC/GRAFO', 'GRAFO'),
      proyecto: buscarCol(cols, 'PROYECTO'),
      destino: buscarCol(cols, 'DESTINO'),
      h1: buscarCol(cols, 'Hora de salida Planta'),
      h2: buscarCol(cols, 'Hora de llegada Obra'),
      h3: buscarCol(cols, 'Hora Inicio de descarga'),
      h4: buscarCol(cols, 'Hora Término descarga', 'Hora Termino descarga'),
      h5: buscarCol(cols, 'Hora salida obra'),
      h6: buscarCol(cols, 'Hora llegada planta'),
      equipo: buscarCol(cols, 'EQUIPO'),
      operador: buscarCol(cols, 'OPERADOR'),
      opPlanta: buscarCol(cols, 'Op Planta'),
      solicita: buscarCol(cols, 'Solicitado por'),
      obs: buscarCol(cols, 'OBS  GUIA OP. MIXER', 'OBS GUIA OP. MIXER', 'OBSERVACIONES'),
    };

    let importadasHoja = 0;
    let filasHoja = 0;
    const ultima = hoja.actualRowCount || hoja.rowCount;

    for (let f = filaEnc + 1; f <= ultima; f++) {
      const fila = hoja.getRow(f);
      const val = (col: number | null) => (col ? fila.getCell(col).value : null);

      const folioRaw = val(c.folio);
      const folio = numero(folioRaw);
      const fechaTxt = aFecha(val(c.fecha));

      // Fila realmente vacía
      if (folio === null && fechaTxt === null && texto(val(c.producto)) === null) continue;
      filasHoja++;

      if (folio === null) {
        inf.omitidas++;
        continue;
      }
      if (fechaTxt === null) {
        inf.sinFecha++;
        inf.omitidas++;
        continue;
      }

      // ── E1: columnas TURNO y N° REMITO invertidas ────────────────────
      let turnoTxt = texto(val(c.turno));
      let remito = numero(val(c.remito));
      const remitoTxt = texto(val(c.remito));
      if (remitoTxt && TURNOS_VALIDOS.has(remitoTxt.toUpperCase())) {
        // el turno vino en la columna del remito
        const turnoComoNumero = numero(val(c.turno));
        turnoTxt = remitoTxt;
        remito = turnoComoNumero;
        inf.columnasCorregidas++;
      }
      const turno: Turno | null =
        turnoTxt && TURNOS_VALIDOS.has(turnoTxt.toUpperCase())
          ? (turnoTxt.toUpperCase() as Turno)
          : null;

      // ── Duplicados y ya cargados ─────────────────────────────────────
      const llave = `${planta}|${folio}`;
      if (yaCargado.has(llave)) {
        inf.duplicadas.push({ folio, planta });
        inf.omitidas++;
        continue;
      }
      yaCargado.add(llave);

      // ── E5: anulaciones ──────────────────────────────────────────────
      const m3Raw = val(c.m3);
      const productoTxt = texto(val(c.producto));
      const anulada =
        esNula(m3Raw) || esNula(productoTxt) || esNula(texto(val(c.cliente)));
      const cantidad = anulada ? 0 : (numero(m3Raw) ?? 0);

      // ── E6: horarios ─────────────────────────────────────────────────
      const mins = [c.h1, c.h2, c.h3, c.h4, c.h5, c.h6].map((col) => aMinutos(val(col)));
      const { instantes, cruces } = reconstruirCiclo(fechaTxt, mins);
      inf.crucesMedianoche += cruces;

      const [i1, i2, i3, i4, i5, i6] = instantes;
      const tiempos = calcularTiempos({
        salidaPlanta: i1 ?? undefined,
        llegadaObra: i2 ?? undefined,
        inicioDescarga: i3 ?? undefined,
        terminoDescarga: i4 ?? undefined,
        salidaObra: i5 ?? undefined,
        llegadaPlanta: i6 ?? undefined,
      });
      if (!tiempos.completa) inf.tiemposIncompletos++;
      inf.bloquesSobrestadia += tiempos.bloquesCodelco ?? 0;

      // ── E9: resolución contra catálogos ──────────────────────────────
      const clienteTxt = texto(val(c.cliente));
      const cliente = clienteTxt ? iCliente.get(clave(clienteTxt)) : undefined;
      if (clienteTxt && !cliente && !anulada) sinCliente.add(clienteTxt);

      const producto = productoTxt
        ? (iProducto.get(clave(productoTxt)) as unknown as DocProducto | undefined)
        : undefined;
      if (productoTxt && !producto && !anulada) sinProducto.add(productoTxt);

      const equipoTxt = texto(val(c.equipo));
      const operadorTxt = texto(val(c.operador));
      const opPlantaTxt = texto(val(c.opPlanta));
      const grafoTxt = texto(val(c.grafo));
      const destinoTxt = texto(val(c.destino));

      const equipo = equipoTxt ? iEquipo.get(clave(equipoTxt)) : undefined;
      const operador = operadorTxt ? iPersona.get(clave(operadorTxt)) : undefined;
      const opPlanta = opPlantaTxt ? iPersona.get(clave(opPlantaTxt)) : undefined;
      const centro = grafoTxt ? iCentro.get(clave(grafoTxt)) : undefined;
      const destino = destinoTxt ? iDestino.get(clave(destinoTxt)) : undefined;

      // ── Valorización ─────────────────────────────────────────────────
      const item = buscarItem(itemDePlanta[planta]);
      const recargos =
        docPlanta && producto && !anulada
          ? calcularRecargos(
              {
                plantaCodigo: planta,
                minCargaM3: docPlanta.minCargaM3,
                cantidadM3: cantidad,
                producto: parsearProducto(producto.codigo),
                generaRecargos: producto.generaRecargos,
                tiempos,
                codigoProductoNormalizado: producto.codigoNormalizado,
              },
              buscarItem,
            )
          : [];

      inf.m3Total += cantidad;
      if (anulada) inf.anuladas++;

      nuevas.push({
        ...metaNueva(null),
        id: uuidv7(),
        plantaId: docPlanta?.id ?? '',
        plantaCodigo: planta,
        sector: docPlanta?.sector ?? 'SUPERFICIE',
        folio,
        remito,
        fecha: fechaTxt,
        turno,
        clienteId: cliente?.id ?? null,
        clienteNombre: cliente?.nombre ?? clienteTxt,
        productoId: producto?.id ?? null,
        productoCodigo: producto?.codigo ?? (productoTxt ? normalizarCodigo(productoTxt) : null),
        cantidadM3: cantidad,
        centroCostoId: centro?.id ?? null,
        centroCostoCodigo: centro?.codigo ?? grafoTxt,
        proyectoId: null,
        proyectoNombre: texto(val(c.proyecto)),
        destinoId: destino?.id ?? null,
        destinoTexto: destino?.nombre ?? destinoTxt,
        equipoId: equipo?.id ?? null,
        equipoCodigo: equipo?.codigo ?? equipoTxt,
        operadorMixerId: operador?.id ?? null,
        operadorMixerNombre: operador?.nombre ?? operadorTxt,
        operadorPlantaId: opPlanta?.id ?? null,
        operadorPlantaNombre: opPlanta?.nombre ?? opPlantaTxt,
        solicitante: texto(val(c.solicita)),
        observaciones: texto(val(c.obs)),
        tsSalidaPlanta: i1?.toISOString() ?? null,
        tsLlegadaObra: i2?.toISOString() ?? null,
        tsInicioDescarga: i3?.toISOString() ?? null,
        tsTerminoDescarga: i4?.toISOString() ?? null,
        tsSalidaObra: i5?.toISOString() ?? null,
        tsLlegadaPlanta: i6?.toISOString() ?? null,
        itemContratoId: item?.id ?? null,
        itemContratoCodigo: item?.codigo ?? null,
        estado: anulada ? 'ANULADA' : producto ? 'EMITIDA' : 'REQUIERE_CLASIFICACION',
        motivoAnulacion: anulada ? 'Marcada como NULA en la planilla de origen' : null,
        estadoPagoId: null,
        origen: 'IMPORT_EXCEL',
        minEstadiaObra: tiempos.estadiaObra,
        minSobreEstadia: tiempos.sobreEstadia,
        bloquesCodelco: tiempos.bloquesCodelco,
        tiemposCompletos: tiempos.completa,
        montoRecargosUf: totalRecargosUf(recargos),
      });

      importadasHoja++;
      inf.importadas++;

      if (inf.importadas % 400 === 0) {
        alProgresar?.(
          12 + Math.round((hojaN / hojasUtiles.length) * 60),
          `Leyendo ${hoja.name}: ${inf.importadas} guías…`,
        );
      }
    }

    inf.hojas.push({ nombre: hoja.name, planta, filas: filasHoja, importadas: importadasHoja });
  }

  alProgresar?.(80, `Guardando ${nuevas.length} guías…`);
  await guardarLote(COL.guias, nuevas);

  inf.sinProducto = [...sinProducto].sort();
  inf.sinCliente = [...sinCliente].sort();

  alProgresar?.(100, 'Listo');
  return inf;
}
